<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import GroupedWeeklyChart from '@/components/charts/GroupedWeeklyChart.vue';
import RecordsWeeklyChart from '@/components/charts/RecordsWeeklyChart.vue';
import SeriesSelector from '@/components/charts/SeriesSelector.vue';
import { ToggleGroup } from '@/components/ui/toggle-group';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { buildWeeklyBins, useGroupedWeeklyAggregation, type GroupedWeeklyRow, type WeeklyBinRange } from '@/composables/records/useGroupedWeeklyAggregation';
import type { RecordWeekBin } from '@/composables/records/useRecordsAggregation';
import { i18n } from '@/plugins/internationalization/i18n';
import colors from '@/plugins/tailwind';
import type { MedicineTreatmentWeek } from '@/types';

/*
 * The Log's treatments per week: one bar per week in the treatment colour, or, per drug, one bar per
 * drug per week with a picker over which drugs are drawn (the top 5 by treatments to begin with), as
 * the delivered feed chart does per Feed Mix. A bar picks its week for the table below; the page
 * owns that selection, this component only reports clicks.
 */
const props = defineProps<{
    weeks: MedicineTreatmentWeek[];
    /** Identifies what `weeks` were fetched for (period and filters); a new one resets the drug picker to the top 5. */
    scopeKey: string;
    selectedWeekStart: string | null;
}>();

const emit = defineEmits<{
    'week-selected': [weekStart: string];
    'selection-reset': [];
}>();

type ChartMode = 'total' | 'byDrug';

const mode = ref<ChartMode>('total');
const modeOptions = computed(() => [
    { value: 'total' as const, label: i18n.global.t('views.health.inventory.chart.total') },
    { value: 'byDrug' as const, label: i18n.global.t('views.health.inventory.chart.byDrug') },
]);

const rows = computed<GroupedWeeklyRow[]>(() => props.weeks.map((week) => ({ week_start: week.week_start, id: week.drug_id, name: week.drug_name, value: week.treatments })));

// Every week of the picked period is on the axis, quiet ones too, so the chart always reads as the period.
const { periodRange } = useInventoryScope();
const range = computed<WeeklyBinRange | null>(() => (periodRange.value.from && periodRange.value.to ? periodRange.value : null));

/** Every drug's treatments of a week added up. */
const totalBins = computed<RecordWeekBin[]>(() =>
    buildWeeklyBins(rows.value, range.value).map((bin) => ({ weekStart: bin.weekStart, timestamp: bin.timestamp, number: [...bin.valueById.values()].reduce((sum, value) => sum + value, 0) }))
);

const { ranking, selectedIds, series, toggle } = useGroupedWeeklyAggregation(rows, toRef(props, 'scopeKey'), range);

/** Animals treated per week, in all and per drug: what a bar's tooltip adds to its count. */
const animalsByWeek = computed(() => {
    const total = new Map<string, number>();
    const byDrug = new Map<string, number>();

    for (const week of props.weeks) {
        total.set(week.week_start, (total.get(week.week_start) ?? 0) + week.animals);
        byDrug.set(`${week.week_start}|${week.drug_id}`, week.animals);
    }

    return { total, byDrug };
});

function animalsText(animals: number | undefined): string | null {
    return animals ? i18n.global.t('views.health.inventory.chart.animals', { count: i18n.global.n(animals, 'decimal') }) : null;
}

const totalDetail = (weekStart: string) => animalsText(animalsByWeek.value.total.get(weekStart));
const drugDetail = (weekStart: string, drugId: number) => animalsText(animalsByWeek.value.byDrug.get(`${weekStart}|${drugId}`));

const drugsLabel = computed(() => i18n.global.t('views.health.inventory.chart.drugs').replace('%SHOWN%', String(selectedIds.value.size)).replace('%TOTAL%', String(ranking.value.length)));
const seriesName = computed(() => i18n.global.t('views.health.inventory.chart.seriesName'));
const title = computed(() => i18n.global.t(mode.value === 'byDrug' ? 'views.health.inventory.chart.headingByDrug' : 'views.health.inventory.chart.heading'));
</script>

<template>
    <GroupedWeeklyChart
        v-if="mode === 'byDrug'"
        :series="series"
        :selected-week-start="selectedWeekStart"
        :colors="colors.chart.treatmentsByDrug"
        :title="title"
        series-id-prefix="drug"
        :point-detail="drugDetail"
        @week-selected="emit('week-selected', $event)"
        @selection-reset="emit('selection-reset')"
    >
        <template #prepend-actions>
            <ToggleGroup v-model="mode" :options="modeOptions" size="sm" :aria-label="$t('views.health.inventory.chart.view')" />
            <SeriesSelector :ranking="ranking" :selected-ids="selectedIds" :label="drugsLabel" @toggle="toggle" />
        </template>
    </GroupedWeeklyChart>
    <RecordsWeeklyChart
        v-else
        :bins="totalBins"
        :selected-week-start="selectedWeekStart"
        :color="colors.chart.treatments"
        :title="title"
        :series-name="seriesName"
        :point-detail="totalDetail"
        @week-selected="emit('week-selected', $event)"
        @selection-reset="emit('selection-reset')"
    >
        <template #prepend-actions>
            <ToggleGroup v-model="mode" :options="modeOptions" size="sm" :aria-label="$t('views.health.inventory.chart.view')" />
        </template>
    </RecordsWeeklyChart>
</template>
